/**
 * What a question will accept, decided before anything is drawn or sent.
 *
 * This is the correctness half of answering, kept away from both the DOM and
 * the network so it can be asserted directly. Two rules live here and nowhere
 * else:
 *
 * 1. **Only a `pending` question takes an answer.** Every other state is either
 *    settled or gone, and offering a control on one of them invites a person to
 *    make a decision the ledger will refuse.
 * 2. **Nothing here ever invents a decision.** There is no default choice, no
 *    "dismiss means no", and no path that submits on the owner's behalf. A
 *    request with no chosen action and no text produces a refusal, not an empty
 *    answer — because an unanswered question is a true statement and a fabricated
 *    answer is not.
 *
 * The server enforces the same rules (`request-service.ts`, `answerRequest`) and
 * remains the authority; this exists so the person is told *before* they commit,
 * and so a stale screen cannot present a control that is going to fail.
 */

import type { LedgerChoice, LedgerRequest } from "./platform-client.js";

/** Whether a question can still be answered, and what to say when it cannot. */
export interface Answerability {
  readonly answerable: boolean;
  /** One line explaining a closed question. Empty when it is open. */
  readonly detail: string;
}

/**
 * Records stored before the prompt-parity contract carry no `actions` and no
 * `allowsFreeform`. They were plain-text questions and must stay answerable as
 * plain text, so an absent flag reads as permitted — which is also the server's
 * own default.
 */
export function choicesOf(request: LedgerRequest): readonly LedgerChoice[] {
  return request.actions ?? [];
}

export function allowsFreeform(request: LedgerRequest): boolean {
  return request.allowsFreeform ?? true;
}

/** True when this question is one the owner is being asked, not one an agent owes. */
export function asksTheOwner(request: LedgerRequest): boolean {
  return request.direction === "to_owner";
}

/**
 * Why a question is closed, or that it is open.
 *
 * **State is read before direction, and that order is canonical** — mirrored by
 * `closedReason` in `frontends/apple/recallvault/RequestAnswer.swift`. A settled
 * record's state is the more specific fact about it: an expired `to_agent`
 * instruction is worth explaining as *expired*, because that is what happened to
 * it, whereas "asked of an agent" merely restates who it was addressed to and
 * would be equally true the whole time it was open. Direction only decides
 * anything while a record is still `pending`.
 */
export function answerability(request: LedgerRequest): Answerability {
  switch (request.state) {
    case "pending":
      return asksTheOwner(request)
        ? { answerable: true, detail: "" }
        : { answerable: false, detail: "This is work asked of an agent. It is answered by whatever picks it up, not here." };
    case "answered":
      return { answerable: false, detail: "This was answered. The answer stands until the process that asked consumes it." };
    case "consumed":
      return { answerable: false, detail: "The answer was used and this question is closed." };
    case "expired":
      return { answerable: false, detail: "This question expired before it was answered. It can no longer be answered." };
    case "withdrawn":
      return { answerable: false, detail: "This question was withdrawn before it was answered." };
    case "superseded":
      return { answerable: false, detail: "A newer version of this question replaced it. Answer the replacement instead." };
    default:
      // A state a newer server added. It is not `pending`, so it is history.
      return { answerable: false, detail: "This question is no longer open." };
  }
}

/** The word a settled question is filed under, matching the state's meaning. */
export function stateLabel(state: LedgerRequest["state"]): string {
  switch (state) {
    case "pending": return "Needs you";
    case "answered": return "Answered";
    case "consumed": return "Resolved";
    case "expired": return "Expired";
    case "withdrawn": return "Withdrawn";
    case "superseded": return "Replaced";
    default: return "Settled";
  }
}

/** What the person has entered so far. Both parts are optional and may be blank. */
export interface AnswerDraft {
  readonly choiceId?: string | null;
  readonly text?: string | null;
}

export type AnswerAttempt =
  | { readonly ok: true; readonly answerAction: string | null; readonly answer: string | null }
  | { readonly ok: false; readonly detail: string };

/**
 * Turns what the person entered into exactly what the ledger will accept, or
 * says why it will not.
 *
 * The blank case is the one that matters: an empty draft is refused rather than
 * being sent as an empty answer, so closing the page, clearing the box, or
 * clicking Submit with nothing selected all leave the question honestly pending.
 */
export function answerAttempt(request: LedgerRequest, draft: AnswerDraft): AnswerAttempt {
  const open = answerability(request);
  if (!open.answerable) return { ok: false, detail: open.detail };

  const text = (draft.text ?? "").trim();
  const choiceId = (draft.choiceId ?? "").trim();
  const choices = choicesOf(request);

  if (choiceId.length > 0 && !choices.some((choice) => choice.id === choiceId)) {
    return { ok: false, detail: "That choice is not one this question offers. Reload the question and choose again." };
  }
  if (text.length > 0 && !allowsFreeform(request)) {
    return { ok: false, detail: "This question takes one of its choices, not typed text." };
  }
  // `text.length` is UTF-16 code units, which is exactly what the server's
  // `bounded()` check counts (`server/src/request-service.ts`). The Swift core
  // counts `utf16.count` for the same reason; neither may use a unit of its own.
  if (text.length > MAX_ANSWER) {
    return { ok: false, detail: `An answer can be at most ${MAX_ANSWER} characters. This one is ${text.length}.` };
  }
  if (choiceId.length === 0 && text.length === 0) {
    return {
      ok: false,
      detail: allowsFreeform(request) && choices.length > 0
        ? "Choose one of the answers or type a reply before submitting."
        : allowsFreeform(request)
          ? "Type a reply before submitting."
          : "Choose one of the answers before submitting.",
    };
  }
  return { ok: true, answerAction: choiceId.length > 0 ? choiceId : null, answer: text.length > 0 ? text : null };
}

/** `FIELD_LIMITS.answer` in `server/src/request-types.ts`. */
export const MAX_ANSWER = 8000;

/** The label a stored decision was made under, preferred over the raw id. */
export function chosenLabel(request: LedgerRequest): string | null {
  const chosen = request.answerAction;
  if (chosen == null) return null;
  return choicesOf(request).find((choice) => choice.id === chosen)?.label ?? chosen;
}
